import {
  createSystem,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react/styled-system';
import { defaultBaseConfig } from '@chakra-ui/react/preset-base';
import { chakraPreset } from './theme/chakraDefaults';
import './assets/fonts/fonts.css';

// Figma redesign tokens (docs/figma-status.md, "Variables → CSS custom
// properties"). Default modes only: Spades · Emerald Felt "Approved dark",
// Spades · Brand accent "Maple", Spades · Team "Team 1". Each colour is a
// Chakra token under `spades.*` and a `--spades-*` CSS var on :root.
const spadesColors = {
  background: '#02121e',
  surface: '#071c29',
  border: '#183f54',
  ivory: '#ffffff',
  muted: '#bdd0cc',
  gold: '#ffbf35',
  'gold-light': '#ffdc67',
  'gold-dark': '#f4a20b',
  pink: '#ff59ba',
  'pink-light': '#ff7aca',
  'pink-dark': '#ff42ac',
  'felt-dark': '#004532',
  lavender: '#bd91ed',
  error: '#ff8068',
  'ivory-warm': '#fffdf8',
  'felt-deep': '#00271d',
  mint: '#bde9ca',
  // Approved accessibility contrast adjustment for small text on the felt.
  'felt-print': '#edfff3',
  'pink-soft': '#ff94d2',
  'wood-shine': '#ee963d',
  'wood-deep': '#301407',
  'gold-amber': '#f6a725',
  'pink-bright': '#ff65bf',
  'border-strong': '#224250',
  'grad-gold-alt-2': '#ffcb43',
  'grad-gold-alt-3': '#ffaa10',
  'grad-gold-1': '#ffe186',
  'grad-gold-2': '#ffd359',
  'grad-gold-3': '#ffbc2b',
  'grad-gold-4': '#ffa40c',
  'grad-pink-1': '#ffa0d6',
  'grad-navy-1': '#577b9c',
  'grad-navy-2': '#345879',
  'grad-navy-3': '#203c59',
  'grad-wood-1': '#c56c2e',
  'grad-wood-2': '#954517',
  'grad-wood-3': '#813914',
  'grad-wood-4': '#612708',
  'grad-wood-alt-1': '#82441e',
  'grad-wood-alt-2': '#683013',
  'grad-wood-alt-3': '#62290f',
  'grad-wood-alt-4': '#4a210e',
  'grad-felt-1': '#087a5e',
  'grad-felt-2': '#006f50',
  'grad-felt-3': '#00533d',
  'accent-base': '#e2b97f',
  'accent-grad-1': '#f0d29c',
  'accent-grad-3': '#cfa065',
  'accent-grad-4': '#b5834a',
  'accent-numeral-dark': '#dfe8ea',
};

// Figma aliases stay aliases. accent/sheen, glint, grain and grain-light have
// no code syntax: they're transparent in the adopted Maple mode.
const spadesColorAliases = {
  'accent-on': 'background',
  'accent-emphasis': 'ivory',
  'accent-frame': 'border-strong',
  'accent-eyebrow-top': 'muted',
  'accent-eyebrow-bottom': 'muted',
  'accent-mark': 'accent-base',
  'accent-edge': 'background',
  'accent-rule': 'accent-on',
  // Code only (2026-10-29): exact duplicates, and shades 1-4 RGB steps from
  // another colour. docs/figma-status.md lists them, and the colours removed
  // because nothing used them, for the matching Figma clean-up.
  'accent-grad-2': 'accent-base',
  'accent-numeral-light': 'ivory',
  'background-inset': 'background',
  'background-raised': 'background',
  'grad-gold-alt-1': 'grad-gold-1',
  'gold-soft': 'gold-light',
  'gold-bright': 'gold',
};

// Spades · Team is a Figma mode: Team 1 on :root, Team 2 under
// [data-team="2"].
const teamModes = {
  1: {
    base: 'gold',
    light: 'gold-light',
    dark: 'gold-dark',
    bright: 'gold-bright',
    soft: 'gold-soft',
    'grad-1': 'grad-gold-1',
    'grad-2': 'grad-gold-2',
    'grad-3': 'grad-gold-3',
    'grad-4': 'grad-gold-4',
  },
  2: {
    base: 'pink',
    light: 'pink-light',
    dark: 'pink-dark',
    bright: 'pink-bright',
    soft: 'pink-soft',
    'grad-1': 'grad-pink-1',
    'grad-2': 'pink-light',
    'grad-3': 'pink',
    'grad-4': 'pink-dark',
  },
};

// Figma families, self-hosted in src/assets/fonts. Chakra token under
// `fonts.spades.*` and `--spades-font-*` var. The UI face is also Chakra's
// body and heading font, so it's the page's font; Anton and Poppins are set
// where they're used.
const spadesFonts = {
  ui: '"Roboto Condensed", "Arial Narrow", system-ui, sans-serif',
  display: 'Anton, Impact, "Arial Narrow", sans-serif',
  brand: 'Poppins, system-ui, sans-serif',
};

// The app's type scale is font-size-* here, not Chakra's fontSizes (whose
// step names mean other sizes). table/oval-height comes from the fit rule
// (chat 13); seat/rim-inset-top stays code-computed.
const spadesDimensions = {
  'font-size-3xs': '10px',
  'font-size-2xs': '12px',
  'font-size-xs': '14px',
  'font-size-sm': '16px',
  'font-size-md': '18px',
  'font-size-lg': '22px',
  'font-size-xl': '28px',
  'font-size-2xl': '36px',
  'font-size-3xl': '56px',
  'spacing-xs': '4px',
  'spacing-sm': '8px',
  'spacing-ms': '12px',
  'spacing-md': '16px',
  'spacing-ml': '20px',
  'spacing-lg': '24px',
  'spacing-xl': '32px',
  'spacing-2xl': '40px',
  'radius-xs': '4px',
  'radius-sm': '8px',
  'radius-control': '10px',
  'radius-panel': '12px',
  'radius-lg': '22px',
  'radius-pill': '9999px',
  'size-control-height': '44px',
  'size-name-height': '36px',
  'stroke-thin': '1px',
  'stroke-medium': '1.5px',
  'stroke-thick': '2px',
  'seat-rim-inset-side': '34px',
  'seat-rim-inset-bottom': '26px',
  'seat-name-max-width': '104px',
  // Spades · Screen width 390 / 375 / 360; narrower below.
  'seat-name-max-width-side': '94px',
  'home-team-name-max': '166px',
};

// Letter-spacing in em, so it follows the font size (Figma's tracking is a
// percentage). Not Figma variables.
const spadesTracking = {
  'tracking-tight': '0.03em',
  'tracking-sm': '0.06em',
  'tracking-md': '0.08em',
  'tracking-lg': '0.1em',
  'tracking-xl': '0.12em',
  'tracking-2xl': '0.16em',
};

const spadesVars = (
  entries: Record<string, string>,
  toValue: (value: string) => string = (value) => value,
) =>
  Object.fromEntries(
    Object.entries(entries).map(([name, value]) => [
      `--spades-${name}`,
      toValue(value),
    ]),
  ) as Record<`--spades-${string}`, string>;
const spadesVar = (name: string) => `var(--spades-${name})`;
const teamVars = (mode: Record<string, string>) =>
  spadesVars(
    Object.fromEntries(
      Object.entries(mode).map(([name, target]) => [`team-${name}`, target]),
    ),
    spadesVar,
  );

// The dashed "inner rule" stitched just inside every action button and key
// (Emerald/Action button 1482:18706, Picker key 1472:18443).
const innerRule = (
  color: string,
  inset: string,
  opacity: number,
  radius: string,
) =>
  ({
    content: '""',
    position: 'absolute',
    inset,
    border: '1.5px dashed',
    borderColor: color,
    opacity,
    borderRadius: radius,
    pointerEvents: 'none',
  }) as const;

const accentGradient = `linear-gradient(180deg, ${spadesVar('accent-grad-1')} 0%, ${spadesVar('accent-grad-2')} 24%, ${spadesVar('accent-grad-3')} 52%, ${spadesVar('accent-grad-4')} 100%)`;

// Pressed looks (src/components/ui/press.ts) are on at once: no transition.
// A press that swaps fill and text colours also comes off at once, with only
// the shrink easing back: a colour fade passes a frame where the two match
// and the label vanishes.
const SWAP_TRANSITION = 'transform 0.2s';

const sunk = {
  transition: 'none',
  filter: 'var(--press-sink)',
  boxShadow: 'var(--press-sink-shadow)',
} as const;

const secondaryButton = {
  bg: spadesVar('background-raised'),
  borderWidth: '1.5px',
  borderStyle: 'solid',
  borderColor: spadesVar('accent-base'),
  color: spadesVar('ivory'),
  minH: '52px',
  _before: innerRule(spadesVar('accent-base'), '3.5px', 0.28, '7px'),
  transition: SWAP_TRANSITION,
  // Colour only: `bg` would also clear the pressed gradient under a mouse.
  _hover: { bgColor: 'spades.surface' },
  // Held, it looks like the primary button. The score-limit radios share
  // this variant, but modals.css keeps their own fill and text.
  _active: {
    transition: 'none',
    backgroundImage: accentGradient,
    borderColor: spadesVar('accent-edge'),
    color: spadesVar('accent-on'),
    _before: innerRule(spadesVar('accent-rule'), '3.5px', 0.35, '7px'),
  },
} as const;

// Picker key (Emerald/Picker key 1472:18443): a dark plaque; the current
// value is the pressed one and takes the team gradient.
const chosenKey = {
  backgroundImage: `linear-gradient(180deg, ${spadesVar('team-grad-1')} 0%, ${spadesVar('team-grad-2')} var(--spades-team-stop-2), ${spadesVar('team-grad-3')} var(--spades-team-stop-3), ${spadesVar('team-grad-4')} 100%)`,
  backgroundColor: 'transparent',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: spadesVar('background'),
  color: spadesVar('background'),
  boxShadow: '0 2px 2px rgba(0, 0, 0, 0.3)',
  _before: innerRule(spadesVar('background'), '3px', 0.35, '6px'),
} as const;

const pickerKeyButton = {
  bg: spadesVar('background-raised'),
  borderWidth: spadesVar('stroke-medium'),
  borderStyle: 'solid',
  borderColor: spadesVar('border-strong'),
  borderRadius: spadesVar('radius-control'),
  color: spadesVar('ivory'),
  h: '60px',
  minH: '60px',
  minW: 0,
  px: '0',
  _before: innerRule(spadesVar('team-base'), '3.5px', 0.4, '6px'),
  // Choosing a key swaps its colours too.
  transition: SWAP_TRANSITION,
  // Colour only: the `bg` shorthand would also clear _pressed's gradient on
  // a hovered key (and iOS keeps :hover after a tap).
  _hover: { bgColor: 'spades.surface' },
  // Held, a key looks chosen; the chosen key sinks.
  _active: { transition: 'none', ...chosenKey, _pressed: sunk },
  _pressed: chosenKey,
} as const;

const buttonRecipe = defineRecipe({
  base: {
    position: 'relative',
    transition: 'all 0.2s',
    borderRadius: '12px',
    fontWeight: '800',
    color: spadesVar('ivory'),
    _active: {
      transform: 'scale(0.98)',
    },
    // The button takes the press, not its icon: Safari drops a held mouse
    // press when the shrink above moves an icon under the pointer.
    '& svg': { pointerEvents: 'none' },
    _disabled: {
      opacity: 0.4,
    },
  },
  variants: {
    variant: {
      secondary: secondaryButton,
      // Quiet: text-only dismiss.
      ghost: {
        bg: 'transparent',
        border: 'none',
        color: spadesVar('muted'),
        fontWeight: '700',
        minH: '52px',
        _hover: {
          bg: 'rgba(255, 255, 255, 0.08)',
          color: spadesVar('ivory'),
        },
        _active: {
          transition: 'none',
          bg: 'var(--press-wash)',
          color: spadesVar('ivory'),
        },
        // Chakra's stock ghost paints aria-expanded a light grey, which only
        // shows on touch screens (hover covers it with a mouse). The header's
        // open menu button stays clear behind its X.
        _expanded: { bg: 'transparent' },
      },
      primary: {
        backgroundImage: accentGradient,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: spadesVar('accent-edge'),
        color: spadesVar('accent-on'),
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        minH: '52px',
        _before: innerRule(spadesVar('accent-rule'), '3px', 0.35, '7px'),
        _hover: {
          filter: 'brightness(1.05)',
        },
        _active: sunk,
      },
      dangerOutline: {
        bg: spadesVar('background-raised'),
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: 'spades.error',
        color: 'spades.error',
        minH: '52px',
        _before: innerRule('{colors.spades.error}', '3.5px', 0.35, '7px'),
        transition: SWAP_TRANSITION,
        _hover: {
          bg: 'spades.error/10',
          borderColor: 'spades.error',
        },
        // Held, a coral fill with dark text: red text on a red tint lost
        // its contrast.
        _active: {
          transition: 'none',
          bgColor: 'spades.error',
          color: spadesVar('background'),
          _before: innerRule(spadesVar('background'), '3.5px', 0.35, '7px'),
        },
      },
      pickerKey: pickerKeyButton,
      // Emerald/Undo Type=Key (1445:17444): the UNDO key in the picker grid.
      undoKey: {
        ...pickerKeyButton,
        bg: spadesVar('background'),
        borderColor: spadesVar('ivory'),
        flexDirection: 'column',
        gap: '3px',
        fontWeight: '700',
        letterSpacing: 'var(--spades-tracking-lg)',
        textTransform: 'uppercase',
        _before: { display: 'none' },
        _hover: { bgColor: 'spades.surface' },
        // Held, it turns ivory with a dark X and label.
        _active: {
          transition: 'none',
          bgColor: spadesVar('ivory'),
          color: spadesVar('background'),
        },
      },
    },
  },
  // Chakra's size text style (textStyle) outranks any font size set earlier in
  // the recipe. Compound variants are merged last, so every Figma font size
  // lives here and nowhere else in the recipe.
  compoundVariants: [
    ...(['secondary', 'primary', 'dangerOutline'] as const).map((variant) => ({
      variant,
      css: { fontSize: spadesVar('font-size-md'), lineHeight: 1.1 },
    })),
    {
      variant: 'ghost',
      css: { fontSize: spadesVar('font-size-sm'), lineHeight: 1.25 },
    },
    {
      variant: 'pickerKey',
      css: { fontSize: spadesVar('font-size-lg'), lineHeight: 1.1 },
    },
    {
      variant: 'undoKey',
      css: { fontSize: spadesVar('font-size-xs'), lineHeight: 1.25 },
    },
  ],
  defaultVariants: {
    variant: 'secondary',
  },
});

export const inputRecipe = defineRecipe({
  base: {
    // 16px minimum: iOS Safari zooms the page when an input below 16px is
    // focused, and the zoom sticks after the keyboard closes.
    fontSize: '16px',
    bg: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    _placeholder: {
      color: 'gray.500',
      opacity: 1,
    },
    _focus: {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      boxShadow: 'none',
    },
    _invalid: {
      borderColor: 'spades.error',
      boxShadow: '0 0 0 1px {colors.spades.error}',
    },
  },
  variants: {
    size: {
      md: {
        textStyle: 'md', // 16px — ensures default md inputs resolve to >= 16px so iOS Safari never zooms
      },
      lg: {
        textStyle: 'lg', // 18px
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const fieldSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'helperText', 'errorText'],
  base: {
    label: {
      color: 'inherit',
      fontSize: '15px',
      fontWeight: '700',
      marginBottom: '6px',
      display: 'block',
      opacity: '0.9',
    },
    errorText: {
      color: 'spades.error',
    },
  },
});

const badgeRecipe = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // The dealer button (design 2C, 2026-10-26; Figma _Explore/Dealer
    // button · 26pt 2942:41099), shared by the board, Home and the dealer
    // picker: a 26pt ivory disc. At line-height 1, Roboto Condensed's D sits
    // on the disc's centre to within 0.2pt.
    borderRadius: 'var(--spades-radius-pill)',
    fontWeight: '800',
    fontSize: '13px',
    lineHeight: 1,
    width: '26px',
    height: '26px',
    flexShrink: 0,
    verticalAlign: 'middle',
  },
  variants: {
    variant: {
      // A background-coloured D, a light inner ring 2.5pt in (navy at 30%)
      // and a soft drop shadow.
      dealer: {
        backgroundColor: 'spades.ivory',
        color: 'spades.background',
        boxShadow:
          '0 2px 4px rgba(0, 0, 0, 0.4), inset 0 0 0 2.5px var(--spades-ivory), inset 0 0 0 3.5px color-mix(in srgb, var(--spades-background) 30%, transparent)',
      },
    },
    // Chakra's preset applies its default size after the variant: a 14px
    // text style, 6px padding and a 20px minimum height. Restate the
    // button's own type and drop the rest.
    size: {
      sm: {
        px: 0,
        minH: 0,
        textStyle: 'none',
        fontSize: '13px',
        lineHeight: 1,
      },
    },
  },
  defaultVariants: {
    variant: 'dealer',
  },
});

export const system = createSystem(defaultBaseConfig, chakraPreset, {
  conditions: {
    // Chakra's hover, minus a held button: under a mouse the pointer is
    // still over it, and the hover look would outrank the pressed one.
    hover: [
      '@media (hover: hover)',
      '&:is(:hover, [data-hover]):not(:disabled, [data-disabled], :active)',
    ],
  },
  theme: {
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      badge: badgeRecipe,
    },
    slotRecipes: {
      field: fieldSlotRecipe,
    },
    tokens: {
      // Chakra's own type scale, for its built-in parts (recipes, text
      // styles, the inputs' 16px floor). App code sizes text with the Spades
      // scale (--spades-font-size-*), whose step names mean other sizes.
      fontSizes: {
        '2xs': { value: '12px' },
        xs: { value: '14px' },
        sm: { value: '15px' },
        md: { value: '16px' },
        lg: { value: '18px' },
        xl: { value: '20px' },
        '2xl': { value: '24px' },
        '3xl': { value: '30px' },
      },
      fonts: {
        body: { value: spadesFonts.ui },
        heading: { value: spadesFonts.ui },
        spades: Object.fromEntries(
          Object.entries(spadesFonts).map(([name, value]) => [name, { value }]),
        ),
      },
      colors: {
        spades: Object.fromEntries(
          Object.entries(spadesColors).map(([name, value]) => [
            name,
            { value },
          ]),
        ),
      },
    },
    semanticTokens: {
      colors: {
        bg: { value: '{colors.spades.background}' },
        fg: { value: '{colors.spades.ivory}' },
      },
    },
  },
  globalCss: {
    'html, body': {
      bg: 'bg',
      color: 'fg',
      height: '100%',
    },
    // Global safety net for mobile form controls: iOS Safari zooms any input
    // with computed font-size < 16px on focus.
    'input, textarea, select': {
      fontSize: '16px',
    },
    ':root': {
      ...spadesVars(spadesColors),
      ...spadesVars(spadesColorAliases, spadesVar),
      ...teamVars(teamModes[1]),
      '--spades-team-stop-2': '24%',
      '--spades-team-stop-3': '52%',
      ...spadesVars(spadesDimensions),
      ...spadesVars(spadesTracking),
      ...spadesVars(
        Object.fromEntries(
          Object.entries(spadesFonts).map(([name, stack]) => [
            `font-${name}`,
            stack,
          ]),
        ),
      ),
      // Spades · Screen width: name limits narrow at 375 and 360.
      '@media (max-width: 389.98px)': {
        '--spades-seat-name-max-width-side': '87px',
        '--spades-home-team-name-max': '158px',
      },
      '@media (max-width: 374.98px)': {
        '--spades-seat-name-max-width-side': '79px',
        '--spades-home-team-name-max': '150px',
      },
      // AppModal, the toasts, the header menu and the watching menu share
      // one panel fill: a lifted navy fading into the surface.
      '--spades-panel-top': '#102b3b',
      '--spades-panel-gradient': `linear-gradient(180deg, ${spadesVar('panel-top')} 0%, ${spadesVar('surface')} 100%)`,
      // Pressed looks (src/components/ui/press.ts).
      '--press-wash': 'rgba(255, 255, 255, 0.14)',
      '--press-sink': 'brightness(0.82)',
      '--press-sink-shadow': 'inset 0 2px 5px rgba(0, 0, 0, 0.35)',
      '--press-dim': '0.5',
      // Seat Bid/Made cards and their hanging bid pill, a little larger on
      // phones (ef609ee); the pill overlaps by 4px. Read by the seats
      // (TableRound) and by Classic's centred error button (classic.css).
      '--player-card-width': '76px',
      '--player-card-height': '44px',
      '--player-bid-height': '28px',
      '@media (max-width: 599.98px), (orientation: landscape) and (max-height: 500px)':
        {
          '--player-card-width': '80px',
          '--player-card-height': '46px',
          '--player-bid-height': '30px',
        },
    },
    '[data-team="2"]': {
      ...teamVars(teamModes[2]),
      '--spades-team-stop-2': '25%',
      '--spades-team-stop-3': '58%',
    },
  },
});
